import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { LogOut, Trash2, RefreshCw, Users, Mail, Phone, MessageSquare } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem("adminToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/leads`, getAuthHeader());
      setLeads(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to fetch leads");
      }
      console.error("Error fetching leads:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, getAuthHeader());
      setAdminInfo(response.data);
    } catch (error) {
      console.error("Error fetching admin info:", error);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchAdminInfo();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus }, getAuthHeader());
      toast.success("Lead status updated");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update lead status");
      console.error("Error updating lead:", error);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      await axios.delete(`${API}/leads/${leadId}`, getAuthHeader());
      toast.success("Lead deleted successfully");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
      console.error("Error deleting lead:", error);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "contacted":
        return "bg-green-100 text-green-800";
      case "qualified":
        return "bg-purple-100 text-purple-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200" data-testid="admin-dashboard-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1
                className="text-3xl font-bold text-[#1A1A1A]"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="dashboard-heading"
              >
                RERA<span className="text-[#C9A14A]">Hub</span>
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Welcome back, {adminInfo?.name || "Admin"}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-none transition-colors duration-300"
              data-testid="admin-logout-button"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 border border-slate-100 shadow-sm rounded-none" data-testid="stat-total-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total Leads</p>
                <p className="text-3xl font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {stats.total}
                </p>
              </div>
              <Users className="w-10 h-10 text-[#C9A14A]" />
            </div>
          </div>

          <div className="bg-white p-6 border border-slate-100 shadow-sm rounded-none" data-testid="stat-new-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">New Leads</p>
                <p className="text-3xl font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {stats.new}
                </p>
              </div>
              <Mail className="w-10 h-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-6 border border-slate-100 shadow-sm rounded-none" data-testid="stat-contacted-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Contacted</p>
                <p className="text-3xl font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {stats.contacted}
                </p>
              </div>
              <Phone className="w-10 h-10 text-green-500" />
            </div>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white border border-slate-100 shadow-sm rounded-none">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h2
              className="text-xl font-semibold text-[#0A1321]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="leads-table-heading"
            >
              All Leads
            </h2>
            <button
              onClick={fetchLeads}
              className="flex items-center gap-2 px-4 py-2 bg-[#C9A14A] hover:bg-[#DFB65A] text-white rounded-none transition-colors duration-300"
              data-testid="refresh-leads-button"
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>

          {isLoading ? (
            <div className="p-12 text-center text-slate-600" data-testid="loading-indicator">
              Loading leads...
            </div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center text-slate-600" data-testid="no-leads-message">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <p>No leads yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead className="bg-[#F5F7FA]">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Email</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Phone</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Service</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Message</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Date</th>
                    <th className="text-left p-4 text-sm font-semibold text-[#0A1321]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, index) => (
                    <tr key={lead.lead_id} className="border-t border-slate-100" data-testid={`lead-row-${index}`}>
                      <td className="p-4 text-sm text-slate-700">{lead.name}</td>
                      <td className="p-4 text-sm text-slate-700">{lead.email}</td>
                      <td className="p-4 text-sm text-slate-700">{lead.phone}</td>
                      <td className="p-4 text-sm text-slate-700">{lead.service_type}</td>
                      <td className="p-4 text-sm text-slate-700 max-w-xs truncate">{lead.message}</td>
                      <td className="p-4">
                        <select
                          value={lead.status}
                          onChange={(e) => handleStatusUpdate(lead.lead_id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(lead.status)} border-0 outline-none cursor-pointer`}
                          data-testid={`lead-status-select-${index}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="p-4 text-sm text-slate-600">{formatDate(lead.created_at)}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleDelete(lead.lead_id)}
                          className="text-red-600 hover:text-red-800 transition-colors duration-300"
                          title="Delete lead"
                          data-testid={`delete-lead-button-${index}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;